import { Controller } from "@hotwired/stimulus"

/**
 * SEO Sections Controller
 * - Fade-in sections on scroll (respects prefers-reduced-motion)
 * - Progress indicator: 1 dot per scroll-snap section, moves 1 dot per section
 */
export default class extends Controller {
  static targets = ["section", "progress", "dot"]

  connect() {
    this.handleScroll = this.handleScroll.bind(this)
    this.observer = null
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.allSections = this.getAllSections()
    this.setupProgressDots()

    if (!this.reducedMotion) {
      this.setupFadeObserver()
      window.addEventListener("scroll", this.handleScroll, { passive: true })
      this.handleScroll()
    } else {
      this.sectionTargets.forEach((el) => el.classList.add("seo-content-section--visible"))
      if (this.hasProgressTarget) this.progressTarget.classList.add("d-none")
    }
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    window.removeEventListener("scroll", this.handleScroll)
  }

  getAllSections() {
    const hero = document.querySelector(".spinshot-hero")
    if (!hero) return []
    const candidates = [
      hero,
      ...document.querySelectorAll(".spinshot-hero ~ section"),
      ...document.querySelectorAll(".seo-content-section"),
      document.querySelector(".website-footer")
    ].filter(Boolean)
    return candidates.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    )
  }

  /* Group sections by dark/light background – 1 dot per logical block */
  getGroups() {
    const darkSelectors = ".spinshot-hero, .seo-content-section, .testimonial-section, .website-footer"
    const isDark = (el) => el.matches && el.matches(darkSelectors)

    const groups = []
    let currentGroup = null

    this.allSections.forEach((section) => {
      const dark = isDark(section)
      if (!currentGroup || currentGroup.dark !== dark) {
        currentGroup = { sections: [section], dark }
        groups.push(currentGroup)
      } else {
        currentGroup.sections.push(section)
      }
    })
    return groups
  }

  setupProgressDots() {
    if (!this.hasProgressTarget || this.allSections.length === 0) return
    const groups = this.getGroups()
    this.progressTarget.innerHTML = ""
    groups.forEach((_, i) => {
      const dot = document.createElement("span")
      dot.className = "seo-progress-indicator__dot"
      dot.setAttribute("data-seo-sections-target", "dot")
      dot.setAttribute("data-index", String(i))
      this.progressTarget.appendChild(dot)
    })
  }

  setupFadeObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("seo-content-section--visible")
          }
        })
      },
      { threshold: 0.15, rootMargin: "0px 0px -50px 0px" }
    )
    this.sectionTargets.forEach((el) => this.observer.observe(el))
  }

  handleScroll() {
    if (!this.hasProgressTarget || this.allSections.length === 0) return

    const dots = this.progressTarget.querySelectorAll(".seo-progress-indicator__dot")
    if (dots.length === 0) return

    const groups = this.getGroups()
    const vh = window.innerHeight
    const scrollY = window.scrollY
    const viewportCenter = scrollY + vh * 0.5

    let activeGroupIndex = 0
    groups.forEach((group, i) => {
      const firstSection = group.sections[0]
      const lastSection = group.sections[group.sections.length - 1]
      const top = firstSection.getBoundingClientRect().top + scrollY
      const bottom = lastSection.getBoundingClientRect().top + scrollY + lastSection.offsetHeight
      if (viewportCenter >= top && viewportCenter <= bottom) activeGroupIndex = i
    })

    dots.forEach((dot, i) => {
      dot.classList.toggle("seo-progress-indicator__dot--active", i === activeGroupIndex)
    })

    const isOverLightSection = this.isViewportCenterOverLightSection(viewportCenter)
    this.progressTarget.classList.toggle("seo-progress-indicator--over-light", isOverLightSection)
  }

  isViewportCenterOverLightSection(viewportCenter) {
    const darkSections = document.querySelectorAll(
      ".spinshot-hero, .seo-content-section, .testimonial-section, .website-footer"
    )
    for (const section of darkSections) {
      const rect = section.getBoundingClientRect()
      const top = rect.top + window.scrollY
      const bottom = top + rect.height
      if (viewportCenter >= top && viewportCenter <= bottom) return false
    }
    return true
  }
}
